import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import PlayerSeat from 'components/PlayerSeat';
import CardDealAnimation from 'components/CardDealAnimation';
import TableSurface from 'components/TableSurface';
import AmbientEffects from 'components/desktop/AmbientEffects';
import DesktopHeader from 'components/desktop/DesktopHeader';
import DesktopActionPanel from 'components/desktop/DesktopActionPanel';
import ChipFlyAnimation from 'components/ChipFlyAnimation';
import PotCollectAnimation from 'components/PotCollectAnimation';
import TableParticles from 'components/table/TableParticles';
import type { ParticleBurstKind } from 'components/table/TableParticles';
import ShowdownBanner from 'components/table/ShowdownBanner';
import SideShowCompareOverlay from 'components/table/SideShowCompareOverlay';
import SharVoteBanner from 'components/table/SharVoteBanner';
import type { SeatChatBubbleItem } from 'components/table/TableSeatChatBubbles';
import { isSharEnabled } from 'lib/shar';
import type { TurnStatus } from 'components/desktop/DesktopHeader';
import { PlayerAction, QUICK_CHAT_PHRASES, CHAT_STICKERS } from 'constants/game';
import { useAuth } from 'context/AuthProvider';
import { formatWalletBreakdown } from 'lib/balance';
import { formatSeatPlayerName } from 'lib/currency-format';
import { useSound } from 'context/SoundProvider';
import { isWinSequenceSound } from 'lib/sound';
import TableCenterHub from 'components/desktop/TableCenterHub';
import DesktopLeftSidebar from 'components/desktop/DesktopLeftSidebar';
import DesktopRightSidebar from 'components/desktop/DesktopRightSidebar';
import MobileSideDock from 'components/desktop/MobileSideDock';
import DistributorTray from 'components/DistributorTray';
import EmptySeat from 'components/EmptySeat';
import {
  buildPlayerSeatAssignments,
  buildEmptySeatMarkers,
  buildSeatLayoutMap,
  seatPositionFor,
  feltSeatNumberFor,
  seatAnchorCoord,
  sameUserId,
  MAX_SEATS,
  CARDS_PER_HAND,
  estimateDealAnimationMs,
  playersBySeat,
  isValidFeltSeatNo,
  type SeatPosition,
  type SeatLayoutProfile,
  type SeatPositionId,
} from 'constants/seatLayout';
import { useTableLayoutMode } from 'hooks/useTableLayoutMode';
import {
  resyncAnimationBaseline,
  seedAnimationBaseline,
  tableAnimationFingerprint,
} from 'lib/table-animation-sync';
import { displayHandCategory, faceUpCardCodes, handCategoryLabel } from 'lib/hand-category';
import { TableChatMessage, TableState, BattleEndedEvent, SideShowResultEvent, PublicPlayerView } from 'types/game';

interface ChipFlyEvent {
  id: number;
  fromSeatNo: number;
  amount: number;
  silent?: boolean;
  /** Play chip SFX on launch even when silent (boot ante — no amount label) */
  withSound?: boolean;
}

interface GameTableProps {
  state: TableState;
  myCards: { code: string }[];
  turnSeconds: number | null;
  turnMaxSeconds?: number;
  handCountdownSeconds?: number | null;
  handCountdownMax?: number;
  setHandCountdownPaused?: (paused: boolean) => void;
  battleResult: BattleEndedEvent | null;
  sideShowResult?: SideShowResultEvent | null;
  onClearBattleResult?: () => void;
  onAction: (action: PlayerAction, amount?: number) => void;
  onLeave: () => void;
  onBack?: () => void;
  onOpenWallet?: () => void;
  chatMessages?: TableChatMessage[];
  onSendChatText?: (text: string) => void;
  onSendChatPhrase?: (phraseId: string) => void;
  onSendChatSticker?: (stickerId: string) => void;
  /** URL room code — shown in header until table state includes roomCode */
  fallbackRoomCode?: string;
}

type WinPhase = 'idle' | 'showdown' | 'collect' | 'crown';
type BootUiPhase = 'idle' | 'chips' | 'pot';

const ACTION_FLASH_MS = 2800;

function roundActionFlashLabel(actionCode: string): string | null {
  const code = String(actionCode || '').toLowerCase();
  if (code === 'chaal2x' || code === 'raise_chaal2x') return 'Chaal 2x';
  if (code === 'chaal' || code === 'raise_chaal') return 'Chaal';
  if (code === 'blind2x' || code === 'raise_blind2x') return 'Blind 2x';
  if (code === 'blind' || code === 'raise_blind') return 'Blind';
  if (code === 'show') return 'Show';
  if (code === 'side_show') return 'Side Show';
  return null;
}

function resolveSeatHandLabel(
  player: PublicPlayerView,
  opts: {
    sideShowResult: SideShowResultEvent | null;
    battleResult: BattleEndedEvent | null;
    stateHandCategory?: string;
    winnerUserId: number | null;
    winPhase: WinPhase;
    hideSeatCards: boolean;
    phase: string;
  }
): string | undefined {
  const {
    sideShowResult,
    battleResult,
    stateHandCategory,
    winnerUserId,
    winPhase,
    hideSeatCards,
    phase,
  } = opts;

  const winRevealActive =
    winPhase === 'showdown' || winPhase === 'collect' || winPhase === 'crown';

  if (hideSeatCards && !winRevealActive) return undefined;

  const inSideShowReveal =
    sideShowResult != null &&
    (sideShowResult.requester.userId === player.userId ||
      sideShowResult.target.userId === player.userId);

  const inActiveHand =
    phase === 'betting' || phase === 'boot' || phase === 'dealing';

  if (inActiveHand) {
    if (inSideShowReveal && sideShowResult) {
      return sideShowResult.requester.userId === player.userId
        ? displayHandCategory(sideShowResult.requester.handCategory)
        : displayHandCategory(sideShowResult.target.handCategory);
    }
    if (player.isSeen) {
      const openCodes = faceUpCardCodes(player.cards);
      if (openCodes.length === 3) {
        return handCategoryLabel(openCodes) ?? undefined;
      }
    }
    return undefined;
  }

  if (!winRevealActive && phase !== 'showdown') return undefined;

  if (winnerUserId != null && player.userId === winnerUserId && winRevealActive) {
    return displayHandCategory(battleResult?.handCategory ?? stateHandCategory);
  }

  const revealedCodes = faceUpCardCodes(player.cards);
  if (revealedCodes.length === 3 && (phase === 'showdown' || winRevealActive)) {
    return handCategoryLabel(revealedCodes) ?? undefined;
  }

  return undefined;
}

/** Winner crown + confetti — must finish before next-hand countdown shows */
const WIN_CROWN_DISPLAY_MS = 2800;
/** On-table chat bubble visibility */
const SEAT_CHAT_BUBBLE_MS = 5200;
/** Hard cap so pot collect never blocks the win sequence */
const POT_COLLECT_SAFETY_MS = 2400;

function isWinSequenceActive(winPhase: WinPhase): boolean {
  return winPhase === 'showdown' || winPhase === 'collect' || winPhase === 'crown';
}

/** True when the center "Next hand" countdown is on screen (Octro card-hide moment). */
function isNextHandTimerVisible(opts: {
  handCountdownSeconds: number | null | undefined;
  handStartCountdown: number | null | undefined;
  sharPhaseActive: boolean;
  handStarting: boolean;
  interRoundUiActive: boolean;
  winPhase: WinPhase;
  potCollecting: boolean;
  serverCelebration: boolean;
}): boolean {
  const handCountdownLive =
    (opts.handCountdownSeconds != null && opts.handCountdownSeconds > 0) ||
    (opts.handStartCountdown != null && opts.handStartCountdown > 0);
  const countdownUiActive = opts.handStarting || opts.interRoundUiActive;
  const winCelebrationBlocking =
    (isWinSequenceActive(opts.winPhase) || opts.potCollecting) && !countdownUiActive;

  return (
    handCountdownLive &&
    !opts.sharPhaseActive &&
    countdownUiActive &&
    !winCelebrationBlocking &&
    !opts.serverCelebration
  );
}

/** Server-driven inter-round phase — avoids replaying win UI during countdown */
function resolveInterRoundSnapshot(state: TableState, now = Date.now()) {
  const inCelebration =
    state.interRoundPhase === 'celebration' ||
    (state.celebrationEndsAt != null && state.celebrationEndsAt > now);

  const inCountdown =
    state.interRoundPhase === 'countdown' ||
    (!inCelebration &&
      ((state.handStartEndsAt != null && state.handStartEndsAt > now) ||
        (state.handStartCountdown != null &&
          state.handStartCountdown > 0 &&
          state.phase !== 'betting' &&
          state.phase !== 'boot' &&
          state.phase !== 'dealing' &&
          state.phase !== 'shar')));

  return { inCelebration, inCountdown };
}

function hydrateWinPhaseFromSnapshot(state: TableState): WinPhase {
  if (state.phase !== 'completed' || state.winnerUserId == null) return 'idle';

  const settledPot = Math.max(0, Number(state.lastHandPotAmount) || 0);
  if (settledPot <= 0) return 'idle';

  const { inCelebration, inCountdown } = resolveInterRoundSnapshot(state);
  if (inCountdown) return 'idle';
  if (inCelebration) return 'crown';
  return 'idle';
}

/** Hide cards when winner label is gone; keep them through showdown / collect / crown. */
function shouldHideCardsAfterWinner(opts: {
  phase: string;
  winPhase: WinPhase;
  handStartCountdown: number | null | undefined;
  interRoundPhase?: 'celebration' | 'countdown' | null;
  waitingForPlayers?: boolean;
  handStarting?: boolean;
  potCollecting?: boolean;
  nextHandTimerVisible?: boolean;
}): boolean {
  if (
    opts.phase === 'boot' ||
    opts.phase === 'dealing' ||
    opts.phase === 'betting' ||
    opts.phase === 'shar'
  ) {
    return false;
  }

  if (Boolean(opts.waitingForPlayers)) return true;
  if (Boolean(opts.handStarting)) return true;
  if (opts.phase === 'waiting') return true;

  // Winner ribbon / crown / showdown announce still on screen
  if (isWinSequenceActive(opts.winPhase) || opts.potCollecting) return false;

  // Octro — hide when winner label clears (same for 1 or many players at table)
  if (opts.phase === 'completed' || opts.phase === 'showdown') {
    return opts.winPhase === 'idle';
  }

  if (opts.nextHandTimerVisible) return true;

  return false;
}

function seatEq(a: number | null | undefined, b: number | null | undefined): boolean {
  return Number(a) === Number(b);
}

function seatAnchorStyle(
  layout: SeatPosition,
  profile: SeatLayoutProfile,
  serverSeatNo?: number
): React.CSSProperties {
  const displaySeatNo = layout.seatNumber ?? feltSeatNumberFor(layout.id);
  const coord = seatAnchorCoord(displaySeatNo, profile, serverSeatNo);
  return {
    left: `${coord.x}%`,
    top: `${coord.y}%`,
  };
}

function emptySeatAnchorStyle(
  layout: SeatPosition,
  profile: SeatLayoutProfile,
  serverSeatNo: number
): React.CSSProperties {
  const displaySeatNo = layout.seatNumber ?? feltSeatNumberFor(layout.id);
  const coord = seatAnchorCoord(displaySeatNo, profile, serverSeatNo);
  return {
    left: `${coord.x}%`,
    top: `${coord.y}%`,
  };
}

function seatScaleClass(mode: 'desktop' | 'mobile-portrait' | 'mobile-landscape'): string {
  if (mode === 'desktop') return 'tp-seat-scale-desktop';
  return 'tp-seat-scale-mobile';
}

function tableStageLayoutClass(): string {
  return ' tp-layout-portrait';
}

const CARD_BACKS = [
  { code: 'back', hidden: true as const },
  { code: 'back', hidden: true as const },
  { code: 'back', hidden: true as const },
];

export default function GameTable({
  state,
  myCards,
  turnSeconds,
  turnMaxSeconds = 15,
  handCountdownSeconds = null,
  handCountdownMax = 10,
  setHandCountdownPaused,
  battleResult,
  sideShowResult,
  onClearBattleResult,
  onAction,
  onLeave,
  onBack,
  onOpenWallet,
  chatMessages = [],
  onSendChatText,
  onSendChatPhrase,
  onSendChatSticker,
  fallbackRoomCode,
}: GameTableProps) {
  const { user } = useAuth();
  const { play, unlock } = useSound();
  const { mode: layoutMode } = useTableLayoutMode();
  const seatProfile: SeatLayoutProfile = 'portrait';
  const seatVariant: 'mobile' | 'desktop' = layoutMode === 'desktop' ? 'desktop' : 'mobile';
  const inlineSeatTurnTimer = false;
  const compactHub = true;
  const lastAnimatedHandRef = useRef(0);
  const animationsLiveRef = useRef(false);
  const hydratedBattleUuidRef = useRef<string | null>(null);
  const hydratedRoundRef = useRef<number>(0);
  const lastAnimFingerprintRef = useRef('');
  const prevPhaseRef = useRef(state.phase);
  const [isDealing, setIsDealing] = useState(false);
  const [dealKey, setDealKey] = useState(0);
  const [justRevealed, setJustRevealed] = useState(false);
  const [dealRevealReady, setDealRevealReady] = useState(false);
  const [dealtCardsBySeat, setDealtCardsBySeat] = useState<Record<number, number>>({});
  const [chipFlies, setChipFlies] = useState<ChipFlyEvent[]>([]);
  const [betFlash, setBetFlash] = useState<{ seatNo: number; amount: number } | null>(null);
  const wasSeenRef = useRef(false);
  const prevPotRef = useRef(0);
  const prevActiveSeatRef = useRef<number | null>(null);
  const bootAnimatedRoundRef = useRef(0);
  const bootChipRoundRef = useRef(0);
  const chipFlyIdRef = useRef(0);
  const [winPhase, setWinPhase] = useState<WinPhase>('idle');
  const [potCollecting, setPotCollecting] = useState(false);
  const [displayPot, setDisplayPot] = useState(0);
  const collectStartedRef = useRef(false);
  const lastRevealedHandCardsRef = useRef<Record<number, { code: string }[]>>({});
  const potCollectDoneRef = useRef(false);
  const prevTimedOutRef = useRef<number | null>(null);
  const particleIdRef = useRef(0);
  const [particleBurst, setParticleBurst] = useState<{
    id: number;
    kind: ParticleBurstKind;
    x: number;
    y: number;
  } | null>(null);
  const chipSoundAtRef = useRef(0);
  const sfxPhaseRef = useRef(state.phase);
  const [bootUiPhase, setBootUiPhase] = useState<BootUiPhase>('idle');
  const bootUiRoundRef = useRef(0);
  const bootUiPhaseRef = useRef<BootUiPhase>('idle');
  const [bootChipsInFlight, setBootChipsInFlight] = useState(false);
  const [heroChatOpen, setHeroChatOpen] = useState(false);
  const [seatChatBubbles, setSeatChatBubbles] = useState<SeatChatBubbleItem[]>([]);
  const seenChatIdsRef = useRef<Set<string>>(new Set());
  const chatHistoryReadyRef = useRef(false);
  const pendingBootChipCountRef = useRef(0);
  const tabHiddenAtRef = useRef<number | null>(null);
  const activeDealHandRef = useRef(0);
  const dealFinishedHandRef = useRef(0);
  const prevBattleIdRef = useRef(state.battleId);

  useEffect(() => {
    bootUiPhaseRef.current = bootUiPhase;
  }, [bootUiPhase]);

  useEffect(() => {
    if (state.battleId === prevBattleIdRef.current) return;
    prevBattleIdRef.current = state.battleId;
    activeDealHandRef.current = 0;
    dealFinishedHandRef.current = 0;
    lastAnimatedHandRef.current = 0;
    bootUiRoundRef.current = 0;
    bootChipRoundRef.current = 0;
    bootAnimatedRoundRef.current = 0;
    setDealtCardsBySeat({});
    setDealRevealReady(false);
    setIsDealing(false);
    setJustRevealed(false);
    wasSeenRef.current = false;
    setWinPhase('idle');
    setPotCollecting(false);
    collectStartedRef.current = false;
    potCollectDoneRef.current = false;
    onClearBattleResult?.();
  }, [state.battleId, onClearBattleResult]);

  useEffect(() => {
    if (state.phase === 'dealing' || state.phase === 'betting') {
      if (bootUiPhase === 'chips') {
        setBootUiPhase('pot');
      }
    }
  }, [state.phase, bootUiPhase]);

  useEffect(() => {
    sfxPhaseRef.current = state.phase;
  }, [state.phase]);

  const playSfx = useCallback(
    (name: Parameters<typeof play>[0]) => {
      if (isWinSequenceSound(name)) {
        play(name);
        return;
      }
      const phase = sfxPhaseRef.current;
      if (phase === 'waiting' || phase === 'boot' || phase === 'dealing') return;
      play(name);
    },
    [play]
  );

  const playChipSound = useCallback(
    (minGapMs = 100) => {
      const now = Date.now();
      if (minGapMs > 0 && now - chipSoundAtRef.current < minGapMs) return;
      chipSoundAtRef.current = now;
      play('chip');
    },
    [play]
  );

  const triggerChipFly = useCallback(
    (fromSeatNo: number, amount: number, silent = false, withSound = false) => {
      const seatCap = state.maxSeats ?? MAX_SEATS;
      if (amount <= 0 || !isValidFeltSeatNo(fromSeatNo, seatCap)) return;
      chipFlyIdRef.current += 1;
      pendingBootChipCountRef.current += 1;
      setChipFlies((flies) => [
        ...flies.slice(-6),
        { id: chipFlyIdRef.current, fromSeatNo, amount, silent, withSound },
      ]);
    },
    [state.maxSeats]
  );

  const triggerBetHighlight = useCallback((seatNo: number, amount: number) => {
    setBetFlash({ seatNo: Number(seatNo), amount });
    window.setTimeout(() => setBetFlash(null), 900);
  }, []);

  const handUiSettled =
    winPhase === 'idle' &&
    !potCollecting &&
    (state.phase === 'waiting' ||
      state.phase === 'completed' ||
      state.phase === 'showdown');

  const displayState = useMemo(() => {
    const revealHands = state.phase === 'showdown' || state.phase === 'completed';
    const fullHandVisible =
      revealHands ||
      (dealRevealReady && !isDealing);
    const progressiveDeal = (serverDealtOrInHand: boolean) =>
      serverDealtOrInHand && !fullHandVisible;
    const winSequenceActive = isWinSequenceActive(winPhase);
    const betweenHands =
      state.phase === 'waiting' ||
      state.phase === 'boot' ||
      state.phase === 'dealing' ||
      (state.phase === 'completed' &&
        state.handStartCountdown != null &&
        state.handStartCountdown > 0 &&
        !winSequenceActive);
    const bootAmt = Math.max(1, Number(state.bootAmount) || 10);
    const eligible = state.players.filter(
      (p) => p.status !== 'left' && Number(p.stack) >= bootAmt
    ).length;
    const waitingForPlayersNow = state.phase === 'waiting' && eligible < 2;
    const handStartingNow =
      state.phase === 'waiting' &&
      eligible >= 2 &&
      state.handStartCountdown != null &&
      state.handStartCountdown > 0;
    const interRoundUiActiveNow =
      state.interRoundPhase === 'countdown' ||
      (state.phase === 'completed' &&
        state.handStartCountdown != null &&
        state.handStartCountdown > 0 &&
        state.interRoundPhase !== 'celebration');
    const serverCelebrationNow =
      state.interRoundPhase === 'celebration' ||
      (state.phase === 'completed' &&
        state.winnerUserId != null &&
        state.interRoundPhase !== 'countdown' &&
        (state.handStartCountdown == null || state.handStartCountdown <= 0));
    const sharPhaseActiveNow = state.phase === 'shar' && isSharEnabled(state.sharAmount);
    const nextHandTimerVisibleNow = isNextHandTimerVisible({
      handCountdownSeconds,
      handStartCountdown: state.handStartCountdown,
      sharPhaseActive: sharPhaseActiveNow,
      handStarting: handStartingNow,
      interRoundUiActive: interRoundUiActiveNow,
      winPhase,
      potCollecting,
      serverCelebration: serverCelebrationNow,
    });
    const cardsHiddenAfterWin = shouldHideCardsAfterWinner({
      phase: state.phase,
      winPhase,
      handStartCountdown: state.handStartCountdown,
      interRoundPhase: state.interRoundPhase,
      waitingForPlayers: waitingForPlayersNow,
      handStarting: handStartingNow,
      potCollecting,
      nextHandTimerVisible: nextHandTimerVisibleNow,
    });
    if (cardsHiddenAfterWin) {
      lastRevealedHandCardsRef.current = {};
    }
    const turnSeat = state.sideShowPending?.targetSeat ?? state.activeSeat;
    const restoreActiveAfterHand = betweenHands || handUiSettled;
    const players = state.players.map((p) => {
      if (cardsHiddenAfterWin) {
        const cleared = {
          ...p,
          cards: undefined,
          currentBet: 0,
          isTurn: false,
          isSeen: false,
          isBlind: true,
        };
        if (restoreActiveAfterHand && p.status === 'folded') {
          return {
            ...cleared,
            status: 'active' as const,
          };
        }
        return cleared;
      }
      if (restoreActiveAfterHand && p.status === 'folded') {
        return {
          ...p,
          status: 'active' as const,
          cards: undefined,
          currentBet: 0,
          isSeen: false,
          isBlind: true,
        };
      }
      if (p.status === 'folded' || p.status === 'left') {
        return { ...p, cards: undefined, currentBet: 0, isTurn: false };
      }
      const isMe = user && sameUserId(p.userId, user.userId);
      const serverDealt = Boolean(p.cards && p.cards.length > 0);
      const inHand =
        state.roundNumber > 0 &&
        p.status === 'active' &&
        (state.phase === 'betting' ||
          state.phase === 'boot' ||
          state.phase === 'dealing');
      if (revealHands && serverDealt) {
        const revealed = (p.cards ?? [])
          .filter((c) => c.code && c.code !== 'back' && !c.hidden)
          .map((c) => ({ code: c.code }));
        if (revealed.length) {
          lastRevealedHandCardsRef.current[p.seatNo] = revealed;
          return { ...p, cards: revealed };
        }
        if (isMe && myCards.length) {
          const mine = myCards.map((c) => ({ code: c.code }));
          lastRevealedHandCardsRef.current[p.seatNo] = mine;
          return { ...p, cards: mine };
        }
      }
      if (revealHands && !cardsHiddenAfterWin) {
        const cached = lastRevealedHandCardsRef.current[p.seatNo];
        if (cached?.length) {
          return { ...p, cards: cached };
        }
        if (isMe && myCards.length) {
          const mine = myCards.map((c) => ({ code: c.code }));
          lastRevealedHandCardsRef.current[p.seatNo] = mine;
          return { ...p, cards: mine };
        }
      }
      const dealtCount = Math.min(CARDS_PER_HAND, dealtCardsBySeat[p.seatNo] ?? 0);
      if (isMe && p.isSeen && myCards.length) {
        if (progressiveDeal(serverDealt || inHand)) {
          if (dealtCount > 0) {
            return {
              ...p,
              cards: myCards.slice(0, dealtCount).map((c) => ({ code: c.code })),
            };
          }
          return { ...p, cards: undefined };
        }
        const revealed = myCards.slice(0, CARDS_PER_HAND).map((c) => ({ code: c.code }));
        return {
          ...p,
          cards: [...revealed, ...CARD_BACKS.slice(revealed.length)].slice(0, CARDS_PER_HAND),
        };
      }
      if (serverDealt || inHand) {
        if (progressiveDeal(true)) {
          if (dealtCount > 0) {
            return { ...p, cards: CARD_BACKS.slice(0, dealtCount) };
          }
          return { ...p, cards: undefined };
        }
        return { ...p, cards: CARD_BACKS };
      }
      return p;
    }).map((p) => {
      if (p.status === 'folded' || p.status === 'left') return p;
      const isTurn =
        (state.phase === 'betting' || Boolean(state.sideShowPending)) &&
        turnSeat != null &&
        Number(p.seatNo) === Number(turnSeat) &&
        p.status === 'active';
      return { ...p, isTurn };
    });
    return { ...state, players };
  }, [state, myCards, user, dealRevealReady, isDealing, dealtCardsBySeat, winPhase, potCollecting, handCountdownSeconds, handUiSettled]);

  const visibleRoundLogActions = useMemo(() => {
    const phase = displayState.phase;
    if (phase === 'waiting' || phase === 'completed' || phase === 'shar') {
      return [];
    }
    return displayState.currentRoundActions ?? [];
  }, [displayState.phase, displayState.currentRoundActions]);

  const [actionFlash, setActionFlash] = useState<{ userId: number; label: string; actionId: number } | null>(
    null
  );
  const lastFlashedActionIdRef = useRef(0);
  const actionFlashTimerRef = useRef<number | null>(null);
  const actionLogRoundKey = `${displayState.roundNumber}:${displayState.actionLogRoundNumber ?? 0}`;

  useEffect(() => {
    lastFlashedActionIdRef.current = 0;
    if (actionFlashTimerRef.current != null) {
      window.clearTimeout(actionFlashTimerRef.current);
      actionFlashTimerRef.current = null;
    }
    setActionFlash(null);
  }, [actionLogRoundKey]);

  useEffect(() => {
    if (actionFlashTimerRef.current != null) {
      window.clearTimeout(actionFlashTimerRef.current);
      actionFlashTimerRef.current = null;
    }

    if (visibleRoundLogActions.length === 0) {
      lastFlashedActionIdRef.current = 0;
      setActionFlash(null);
      return;
    }

    const latest = visibleRoundLogActions.reduce((best, entry) =>
      Number(entry.id) > Number(best.id) ? entry : best
    );
    const actionId = Number(latest.id);
    if (!Number.isFinite(actionId) || actionId <= 0) return;
    if (lastFlashedActionIdRef.current === actionId) return;

    const label = roundActionFlashLabel(String(latest.actionCode || ''));
    if (!label) {
      setActionFlash(null);
      return;
    }

    const createdAtMs = Date.parse(String(latest.createdAt || ''));
    if (Number.isFinite(createdAtMs) && Date.now() - createdAtMs > ACTION_FLASH_MS) return;

    lastFlashedActionIdRef.current = actionId;
    const userId = Number(latest.userId);
    setActionFlash({ userId, label, actionId });

    actionFlashTimerRef.current = window.setTimeout(() => {
      setActionFlash(null);
      actionFlashTimerRef.current = null;
    }, ACTION_FLASH_MS);
  }, [visibleRoundLogActions]);

  useEffect(
    () => () => {
      if (actionFlashTimerRef.current != null) {
        window.clearTimeout(actionFlashTimerRef.current);
      }
    },
    []
  );

  const seatedPlayers = useMemo(() => {
    const max = displayState.maxSeats ?? MAX_SEATS;
    const seen = new Set<number>();
    return playersBySeat(
      displayState.players.filter((p) => {
        if (p.status === 'left') return false;
        if (!isValidFeltSeatNo(p.seatNo, max)) return false;
        const id = Number(p.userId);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
    );
  }, [displayState.players, displayState.maxSeats]);

  const maxSeats = displayState.maxSeats ?? MAX_SEATS;
  const me = displayState.players.find((p) => sameUserId(p.userId, user?.userId));
  const headerBalance = useMemo(() => {
    if (!user) return undefined;
    if (me != null && me.status !== 'left') {
      return formatWalletBreakdown(user, me.stack).main;
    }
    return formatWalletBreakdown(user).main;
  }, [user, me?.stack, me?.status]);
  const seatLayoutMap = useMemo(
    () => buildSeatLayoutMap(seatedPlayers, user?.userId, seatProfile),
    [seatedPlayers, user?.userId, seatProfile]
  );
  const seatAssignments = useMemo(
    () => buildPlayerSeatAssignments(seatedPlayers, maxSeats, user?.userId, seatProfile),
    [seatedPlayers, maxSeats, user?.userId, seatProfile]
  );
  const emptySeatMarkers = useMemo(
    () => buildEmptySeatMarkers(seatedPlayers, maxSeats, user?.userId, seatProfile),
    [seatedPlayers, maxSeats, user?.userId, seatProfile]
  );

  const seatIdByUserId = useMemo(() => {
    const map = new Map<number, SeatPositionId>();
    seatAssignments.forEach(({ player, layout }) => {
      if (player) map.set(Number(player.userId), layout.id);
    });
    return map;
  }, [seatAssignments]);

  const seatChatBubbleByUserId = useMemo(() => {
    const map = new Map<number, SeatChatBubbleItem>();
    seatChatBubbles.forEach((bubble) => map.set(Number(bubble.userId), bubble));
    return map;
  }, [seatChatBubbles]);

  const showSeatChatBubble = useCallback(
    (item: Omit<SeatChatBubbleItem, 'seatId'> & { seatId?: SeatPositionId }) => {
      const seatId = item.seatId ?? seatIdByUserId.get(Number(item.userId));
      if (!seatId) return;
      const bubble: SeatChatBubbleItem = {
        id: item.id,
        userId: Number(item.userId),
        text: item.text,
        kind: item.kind,
        stickerId: item.stickerId,
        seatId,
      };
      setSeatChatBubbles((prev) => {
        const filtered = prev.filter((entry) => Number(entry.userId) !== bubble.userId);
        return [...filtered, bubble];
      });
      window.setTimeout(() => {
        setSeatChatBubbles((prev) => prev.filter((entry) => entry.id !== bubble.id));
      }, SEAT_CHAT_BUBBLE_MS);
    },
    [seatIdByUserId]
  );

  useEffect(() => {
    if (chatMessages.length === 0) {
      chatHistoryReadyRef.current = true;
      return;
    }

    if (!chatHistoryReadyRef.current) {
      chatMessages.forEach((msg) => seenChatIdsRef.current.add(msg.id));
      chatHistoryReadyRef.current = true;
      return;
    }

    const fresh = chatMessages.filter((msg) => !seenChatIdsRef.current.has(msg.id));
    if (fresh.length === 0) return;

    fresh.forEach((msg) => {
      seenChatIdsRef.current.add(msg.id);
      showSeatChatBubble({
        id: msg.id,
        userId: Number(msg.userId),
        text: msg.text,
        kind: msg.kind,
        stickerId: msg.stickerId,
      });
    });
  }, [chatMessages, showSeatChatBubble]);

  const dealSeatNosKey = useMemo(() => {
    return seatedPlayers
      .filter((p) => p.status === 'active')
      .map((p) => p.seatNo)
      .sort((a, b) => a - b)
      .join(',');
  }, [seatedPlayers]);

  const dealSeatNos = useMemo(
    () => (dealSeatNosKey ? dealSeatNosKey.split(',').map(Number) : []),
    [dealSeatNosKey]
  );

  const isSideShowTarget =
    displayState.sideShowPending != null &&
    me != null &&
    seatEq(me.seatNo, displayState.sideShowPending.targetSeat);
  /** Lock footer only during boot/deal animation — not after backend is already in betting. */
  const actionPanelDealLocked =
    isDealing ||
    state.phase === 'dealing' ||
    (state.phase === 'boot' && bootUiPhase !== 'pot');
  const isViewerActiveInBetting =
    user?.userId != null &&
    displayState.phase === 'betting' &&
    (displayState.sideShowPending
      ? isSideShowTarget
      : sameUserId(displayState.activePlayerUserId, user.userId));
  const isMyTurn =
    isViewerActiveInBetting &&
    me?.status === 'active' &&
    !actionPanelDealLocked;

  const isPlayerTurn = useCallback(
    (p: { userId: number; seatNo: number; isTurn?: boolean }) => {
      if (displayState.phase !== 'betting' && !displayState.sideShowPending) return false;
      if (displayState.sideShowPending) {
        return Number(p.seatNo) === Number(displayState.sideShowPending.targetSeat);
      }
      if (displayState.activePlayerUserId != null) {
        return sameUserId(p.userId, displayState.activePlayerUserId);
      }
      return false;
    },
    [displayState.phase, displayState.sideShowPending, displayState.activePlayerUserId]
  );
  const activeCount = displayState.players.filter((p) => p.status === 'active').length;
  const seatedCount = displayState.players.filter((p) => p.status !== 'left').length;
  const bootAmount = Math.max(1, Number(displayState.bootAmount) || 10);
  const betLevel = Math.max(bootAmount, Number(displayState.currentStake) || bootAmount);
  const eligibleCount = useMemo(
    () =>
      displayState.players.filter(
        (p) => p.status !== 'left' && Number(p.stack) >= bootAmount
      ).length,
    [displayState.players, bootAmount]
  );
  const waitingForPlayers = state.phase === 'waiting' && eligibleCount < 2;
  const handStarting =
    state.phase === 'waiting' &&
    eligibleCount >= 2 &&
    state.handStartCountdown != null &&
    state.handStartCountdown > 0;
  const sharEnabled = isSharEnabled(displayState.sharAmount);
  const sharPhaseActive = displayState.phase === 'shar' && sharEnabled;
  const sharVoted =
    user?.userId != null && displayState.sharVotes?.[String(user.userId)] != null;
  const sharVote = user?.userId != null ? displayState.sharVotes?.[String(user.userId)] : undefined;
  const canActShar = sharPhaseActive && me?.status === 'active' && !sharVoted;
  const inBetting = displayState.phase === 'betting';
  const dealUiComplete = dealRevealReady && !isDealing;
  const actionsDisabled =
    me?.status === 'folded' ||
    (!inBetting && ((state.phase === 'dealing' && !dealUiComplete) || state.phase === 'boot')) ||
    state.phase === 'showdown' ||
    state.phase === 'completed' ||
    (sharPhaseActive && !canActShar);
  const revealHands = state.phase === 'showdown' || state.phase === 'completed';
  const backendInterRound =
    state.interRoundPhase === 'countdown' ||
    (state.phase === 'completed' &&
      state.handStartCountdown != null &&
      state.handStartCountdown > 0 &&
      state.interRoundPhase !== 'celebration');
  const serverCelebration =
    state.interRoundPhase === 'celebration' ||
    (state.phase === 'completed' &&
      state.winnerUserId != null &&
      state.interRoundPhase !== 'countdown' &&
      (state.handStartCountdown == null || state.handStartCountdown <= 0));
  const winSequenceActive = isWinSequenceActive(winPhase);
  /** Next-hand UI — driven by server interRoundPhase countdown */
  const interRoundUiActive = backendInterRound;
  const winnerUserId = battleResult?.winnerUserId ?? state.winnerUserId ?? null;
  const winnerPlayer = winnerUserId
    ? displayState.players.find((p) => p.userId === winnerUserId)
    : undefined;
  const handCardsFullyVisible =
    revealHands ||
    (dealRevealReady && !isDealing);
  const nextHandTimerVisible = isNextHandTimerVisible({
    handCountdownSeconds,
    handStartCountdown: state.handStartCountdown,
    sharPhaseActive,
    handStarting,
    interRoundUiActive,
    winPhase,
    potCollecting,
    serverCelebration,
  });
  const hideSeatCards = shouldHideCardsAfterWinner({
    phase: state.phase,
    winPhase,
    handStartCountdown: state.handStartCountdown,
    interRoundPhase: state.interRoundPhase,
    waitingForPlayers,
    handStarting,
    potCollecting,
    nextHandTimerVisible,
  });
  const dealAnimationActive = isDealing && !revealHands && !backendInterRound;
  const showDealingOverlay = dealAnimationActive;

  const animationBaselineRefs = useMemo(
    () => ({
      prevPot: prevPotRef,
      prevActiveSeat: prevActiveSeatRef,
      bootAnimatedRound: bootAnimatedRoundRef,
      bootChipRound: bootChipRoundRef,
      lastAnimatedHand: lastAnimatedHandRef,
      prevTimedOut: prevTimedOutRef,
      wasSeen: wasSeenRef,
      collectStarted: collectStartedRef,
      prevPhase: prevPhaseRef,
    }),
    []
  );

  const resyncAnimations = useCallback(() => {
    resyncAnimationBaseline(state, animationBaselineRefs, bootAmount, me?.isSeen ?? false);
    lastAnimFingerprintRef.current = tableAnimationFingerprint(state);
    setChipFlies([]);
    pendingBootChipCountRef.current = 0;
  }, [state, bootAmount, me?.isSeen, animationBaselineRefs]);

  useEffect(() => {
    const resetWinUi = () => {
      setWinPhase('idle');
      setPotCollecting(false);
      collectStartedRef.current = false;
      potCollectDoneRef.current = false;
      lastRevealedHandCardsRef.current = {};
      onClearBattleResult?.();
    };

    if (hydratedBattleUuidRef.current !== state.battleUuid) {
      hydratedBattleUuidRef.current = state.battleUuid;
      hydratedRoundRef.current = state.roundNumber;
      animationsLiveRef.current = false;
      lastAnimFingerprintRef.current = '';
      setBootUiPhase('idle');
      bootUiRoundRef.current = 0;
      bootChipRoundRef.current = 0;
      bootAnimatedRoundRef.current = 0;
      setBootChipsInFlight(false);
      setChipFlies([]);
      pendingBootChipCountRef.current = 0;
      resetWinUi();
    } else if (
      hydratedRoundRef.current !== state.roundNumber &&
      state.roundNumber > 0
    ) {
      hydratedRoundRef.current = state.roundNumber;
      resetWinUi();
    }

    const fingerprint = tableAnimationFingerprint(state);

    if (!animationsLiveRef.current) {
      seedAnimationBaseline(state, animationBaselineRefs, bootAmount, me?.isSeen ?? false);
      animationsLiveRef.current = true;
      lastAnimFingerprintRef.current = fingerprint;
      setChipFlies([]);
      pendingBootChipCountRef.current = 0;

      if (state.battleId > 0 && state.phase !== 'waiting' && state.phase !== 'shar') {
        const midHand =
          state.phase === 'betting' ||
          state.phase === 'showdown' ||
          (state.phase === 'completed' && state.handStartCountdown != null);
        if (midHand) {
          setDealRevealReady(true);
          setIsDealing(false);
          dealFinishedHandRef.current = state.battleId;
          activeDealHandRef.current = state.battleId;
          lastAnimatedHandRef.current = state.battleId;
          if (state.phase === 'betting') {
            setBootUiPhase('pot');
          }
        } else {
          setDealRevealReady(false);
          setIsDealing(false);
          dealFinishedHandRef.current = 0;
          activeDealHandRef.current = 0;
          lastAnimatedHandRef.current = 0;
        }
      }
      if (state.phase === 'showdown') {
        setWinPhase('showdown');
        setDisplayPot(state.potAmount);
      } else if (state.phase === 'completed' && state.winnerUserId != null) {
        const settledPot = Math.max(0, Number(state.lastHandPotAmount) || 0);
        setDisplayPot(settledPot);
        setPotCollecting(false);
        collectStartedRef.current = true;
        setWinPhase(hydrateWinPhaseFromSnapshot(state));
      }
      prevPhaseRef.current = state.phase;
      return;
    }

    if (fingerprint === lastAnimFingerprintRef.current) return;
    lastAnimFingerprintRef.current = fingerprint;
  }, [state, bootAmount, me?.isSeen, animationBaselineRefs, seatProfile, onClearBattleResult]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        tabHiddenAtRef.current = Date.now();
        return;
      }
      if (document.visibilityState === 'visible') {
        tabHiddenAtRef.current = null;
        resyncAnimations();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [resyncAnimations]);

  useEffect(() => {
    if (state.phase === 'boot' || state.phase === 'dealing' || state.phase === 'betting' || state.phase === 'shar' || state.phase === 'waiting') {
      if (state.roundNumber > 0 || state.phase === 'waiting') {
        setWinPhase('idle');
        setPotCollecting(false);
        collectStartedRef.current = false;
      }
    }
  }, [state.phase, state.roundNumber]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (state.roundNumber <= 0) return;
    if (state.phase === 'dealing' || state.phase === 'betting') {
      if (bootUiPhase === 'chips') {
        setBootUiPhase('pot');
      }
      if (bootChipRoundRef.current !== state.roundNumber) {
        bootChipRoundRef.current = state.roundNumber;
      }
    }
    if (state.phase === 'betting' && state.battleId > 0 && prevPhaseRef.current !== 'betting') {
      setBootUiPhase('pot');
      bootChipRoundRef.current = state.roundNumber;

      if (dealFinishedHandRef.current === state.battleId) {
        setDealRevealReady(true);
        setIsDealing(false);
        return;
      }

      if (isDealing && activeDealHandRef.current === state.battleId) {
        return;
      }

      if (
        state.roundNumber === 1 &&
        dealFinishedHandRef.current !== state.battleId &&
        activeDealHandRef.current !== state.battleId
      ) {
        lastAnimatedHandRef.current = state.battleId;
        activeDealHandRef.current = state.battleId;
        dealFinishedHandRef.current = 0;
        setDealtCardsBySeat({});
        setDealRevealReady(false);
        setDealKey((k) => k + 1);
        setIsDealing(true);
      }
    }
  }, [state.phase, state.roundNumber, state.battleId, bootUiPhase, dealSeatNos, isDealing]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (state.phase === 'boot' && state.roundNumber > 0 && bootUiRoundRef.current !== state.roundNumber) {
      bootUiRoundRef.current = state.roundNumber;
      setBootUiPhase('chips');
      setBootChipsInFlight(true);
      setDealRevealReady(false);
      setIsDealing(false);
      pendingBootChipCountRef.current = 0;

      const active = seatedPlayers.filter((p) => p.status === 'active' || p.status === 'all_in');
      active.forEach((p, i) => {
        const amt = p.currentBet > 0 ? p.currentBet : bootAmount;
        if (amt <= 0) return;
        window.setTimeout(() => triggerChipFly(p.seatNo, amt, true, true), i * 160);
      });

      if (active.length === 0) {
        setBootUiPhase('pot');
        setBootChipsInFlight(false);
        prevPotRef.current = state.potAmount;
        bootChipRoundRef.current = state.roundNumber;
      }
    }
  }, [state.phase, state.roundNumber, state.potAmount, seatedPlayers, bootAmount, triggerChipFly]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (state.phase === 'boot' && state.roundNumber > 0 && prevPhaseRef.current !== 'boot') {
      bootAnimatedRoundRef.current = 0;
      setJustRevealed(false);
      setDealtCardsBySeat({});
      setDealRevealReady(false);
      setIsDealing(false);
      wasSeenRef.current = false;
      collectStartedRef.current = false;
      prevTimedOutRef.current = null;
      onClearBattleResult?.();
    }
  }, [state.phase, state.roundNumber, onClearBattleResult]);

  useEffect(() => {
    if (state.phase === 'completed' || state.phase === 'showdown' || state.phase === 'waiting') {
      setIsDealing(false);
      if (state.phase === 'waiting' || state.phase === 'completed') {
        lastAnimatedHandRef.current = 0;
        activeDealHandRef.current = 0;
        dealFinishedHandRef.current = 0;
        setDealRevealReady(false);
        setDealtCardsBySeat({});
        setBootUiPhase('idle');
        bootUiRoundRef.current = 0;
        setBootChipsInFlight(false);
      }
    }
    if (state.phase === 'completed' || state.phase === 'waiting') {
      prevTimedOutRef.current = null;
    }
  }, [state.phase]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (state.battleId <= 0 || backendInterRound) return;
    if (state.phase !== 'dealing') return;
    if (dealSeatNos.length < 2) return;
    if (activeDealHandRef.current === state.battleId) return;
    if (lastAnimatedHandRef.current === state.battleId) return;

    lastAnimatedHandRef.current = state.battleId;
    activeDealHandRef.current = state.battleId;
    dealFinishedHandRef.current = 0;
    setDealtCardsBySeat({});
    setDealRevealReady(false);
    setDealKey((k) => k + 1);
    setIsDealing(true);
  }, [state.battleId, state.phase, backendInterRound, dealSeatNosKey, dealSeatNos.length]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;

    if (state.phase !== 'betting' && state.phase !== 'boot' && state.phase !== 'dealing') {
      prevPotRef.current = state.potAmount;
      prevActiveSeatRef.current = state.activeSeat;
      return;
    }

    if (state.phase === 'boot' || state.phase === 'dealing') {
      prevPotRef.current = state.potAmount;
      prevActiveSeatRef.current = state.activeSeat;
      return;
    }

    const prevPot = prevPotRef.current;
    const actingSeat = prevActiveSeatRef.current ?? state.activeSeat;

    if (
      state.phase === 'betting' &&
      state.potAmount > prevPot &&
      actingSeat != null &&
      state.roundNumber > 0
    ) {
      const delta = state.potAmount - prevPot;
      if (tabHiddenAtRef.current == null) {
        triggerChipFly(actingSeat, delta);
        triggerBetHighlight(actingSeat, delta);
      }
    }

    prevPotRef.current = state.potAmount;
    prevActiveSeatRef.current = state.activeSeat;
  }, [
    state.potAmount,
    state.activeSeat,
    state.phase,
    state.roundNumber,
    triggerChipFly,
    triggerBetHighlight,
  ]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    const round = state.roundNumber;
    if (round <= 0 || round === bootAnimatedRoundRef.current) return;
    if (state.potAmount <= 0) return;
    if (state.phase !== 'betting' && state.phase !== 'boot' && state.phase !== 'dealing') return;

    bootAnimatedRoundRef.current = round;
    prevPotRef.current = state.potAmount;
  }, [state.roundNumber, state.potAmount, state.phase]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    const seen = me?.isSeen ?? false;
    if (seen && !wasSeenRef.current && myCards.length > 0) {
      setJustRevealed(true);
      playSfx('flip');
      const t = setTimeout(() => setJustRevealed(false), 700);
      return () => clearTimeout(t);
    }
    wasSeenRef.current = seen;
  }, [me?.isSeen, myCards.length, playSfx]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (state.phase !== 'showdown' || prevPhaseRef.current === 'showdown') return;
    setWinPhase('showdown');
    setDisplayPot(state.potAmount);
    collectStartedRef.current = false;
    potCollectDoneRef.current = false;
    playSfx('showdown');
    particleIdRef.current += 1;
    setParticleBurst({ id: particleIdRef.current, kind: 'showdown', x: 50, y: 48 });
  }, [state.phase, state.potAmount, playSfx]);

  /** Fallback — opponent leave can race completed; battle_ended still carries pot/winner */
  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (!battleResult?.winnerUserId) return;
    if (collectStartedRef.current || isWinSequenceActive(winPhase)) return;
    if (resolveInterRoundSnapshot(state).inCountdown) return;

    const potAmt = Math.max(0, Number(battleResult.potAmount) || 0);
    if (potAmt <= 0) return;

    const phaseOk =
      state.phase === 'completed' ||
      state.phase === 'showdown' ||
      (state.phase === 'waiting' && battleResult.winReason === 'last_standing');
    if (!phaseOk || winPhase !== 'idle') return;

    setWinPhase('showdown');
    setDisplayPot(potAmt);
    collectStartedRef.current = false;
    potCollectDoneRef.current = false;
    playSfx('showdown');
    particleIdRef.current += 1;
    setParticleBurst({ id: particleIdRef.current, kind: 'showdown', x: 50, y: 48 });
  }, [battleResult, state, winPhase, playSfx]);

  const triggerWinCelebration = useCallback(() => {
    setWinPhase('crown');
    const uid = battleResult?.winnerUserId ?? state.winnerUserId;
    const seatNo = state.players.find((p) => p.userId === uid)?.seatNo;
    if (seatNo != null) {
      const pos = seatPositionFor(seatNo, seatLayoutMap);
      particleIdRef.current += 1;
      setParticleBurst({ id: particleIdRef.current, kind: 'win', x: pos.x, y: pos.y });
    } else {
      particleIdRef.current += 1;
      setParticleBurst({ id: particleIdRef.current, kind: 'win', x: 50, y: 50 });
    }
    playSfx('celebration');
  }, [playSfx, battleResult, state.winnerUserId, state.players, seatLayoutMap]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;

    const { inCountdown } = resolveInterRoundSnapshot(state);
    if (inCountdown) return;

    const collectPhaseOk =
      state.phase === 'completed' ||
      (state.phase === 'waiting' && battleResult != null);
    if (!collectPhaseOk) return;

    const uid = battleResult?.winnerUserId ?? state.winnerUserId;
    if (!uid || collectStartedRef.current) return;

    const potAmt = Math.max(
      0,
      Number(battleResult?.potAmount ?? state.potAmount ?? displayPot) || 0
    );
    if (potAmt <= 0) return;

    const winnerSeat = state.players.find((p) => p.userId === uid)?.seatNo;
    collectStartedRef.current = true;
    potCollectDoneRef.current = false;
    setChipFlies([]);
    setDisplayPot(potAmt);

    if (winnerSeat != null) {
      setPotCollecting(true);
      setWinPhase('collect');
    } else {
      setDisplayPot(0);
      playSfx('win');
      triggerWinCelebration();
    }
  }, [
    state,
    battleResult,
    displayPot,
    playSfx,
    triggerWinCelebration,
  ]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (winPhase !== 'crown') return;
    const delay =
      state.celebrationEndsAt != null
        ? Math.max(0, state.celebrationEndsAt - Date.now())
        : WIN_CROWN_DISPLAY_MS;
    const t = window.setTimeout(() => {
      setWinPhase('idle');
      onClearBattleResult?.();
    }, delay);
    return () => window.clearTimeout(t);
  }, [winPhase, state.celebrationEndsAt, onClearBattleResult]);

  useEffect(() => {
    if (state.phase === 'boot' || state.phase === 'dealing') {
      setWinPhase('idle');
      setPotCollecting(false);
      collectStartedRef.current = false;
      potCollectDoneRef.current = false;
      setDisplayPot(0);
      onClearBattleResult?.();
      return;
    }
    if (state.phase !== 'waiting') return;
    if (isWinSequenceActive(winPhase) || potCollecting) return;

    setWinPhase('idle');
    setPotCollecting(false);
    setDisplayPot(0);
    collectStartedRef.current = false;
    onClearBattleResult?.();
  }, [state.phase, winPhase, potCollecting, onClearBattleResult]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    if (state.lastTimedOutUserId == null) return;
    const timedOutId = state.lastTimedOutUserId;
    if (timedOutId === prevTimedOutRef.current) return;
    prevTimedOutRef.current = timedOutId;
    playSfx('pack');
  }, [state.lastTimedOutUserId, playSfx]);

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    prevPhaseRef.current = state.phase;
  }, [state.phase]);

  const onPotCollectDone = useCallback(() => {
    if (potCollectDoneRef.current) return;
    potCollectDoneRef.current = true;
    setPotCollecting(false);
    setDisplayPot(0);
    playSfx('win');
    triggerWinCelebration();
  }, [triggerWinCelebration, playSfx]);

  useEffect(() => {
    if (!potCollecting) return;
    const t = window.setTimeout(() => onPotCollectDone(), POT_COLLECT_SAFETY_MS);
    return () => window.clearTimeout(t);
  }, [potCollecting, onPotCollectDone]);

  const onPotCollectStart = useCallback(() => {
    playSfx('pot');
  }, [playSfx]);

  const onPotCollectChipLaunch = useCallback(() => {
    playChipSound(90);
  }, [playChipSound]);

  const onDealComplete = useCallback(() => {
    const hand = activeDealHandRef.current;
    if (hand <= 0 || dealFinishedHandRef.current === hand) return;
    dealFinishedHandRef.current = hand;
    setDealtCardsBySeat((prev) => {
      const next = { ...prev };
      for (const seatNo of dealSeatNos) {
        next[seatNo] = CARDS_PER_HAND;
      }
      return next;
    });
    setIsDealing(false);
    setDealRevealReady(true);
  }, [dealSeatNos]);

  useEffect(() => {
    if (!isDealing) return;
    if (activeDealHandRef.current !== state.battleId) return;

    const ms = estimateDealAnimationMs(dealSeatNos.length) + 1200;
    const t = window.setTimeout(() => {
      if (
        activeDealHandRef.current === state.battleId &&
        dealFinishedHandRef.current !== state.battleId
      ) {
        onDealComplete();
      }
    }, ms);
    return () => window.clearTimeout(t);
  }, [isDealing, state.battleId, dealSeatNos.length, onDealComplete]);

  useEffect(() => {
    if (state.phase !== 'betting' || state.battleId <= 0) return;
    if (dealUiComplete || isDealing) return;
    if (dealFinishedHandRef.current === state.battleId) return;
    if (activeDealHandRef.current === state.battleId) return;
    dealFinishedHandRef.current = state.battleId;
    activeDealHandRef.current = state.battleId;
    setDealtCardsBySeat((prev) => {
      const next = { ...prev };
      for (const seatNo of dealSeatNos) {
        next[seatNo] = CARDS_PER_HAND;
      }
      return next;
    });
    setIsDealing(false);
    setDealRevealReady(true);
  }, [state.phase, state.battleId, dealUiComplete, isDealing, dealSeatNos]);

  const onChipFlyDone = useCallback(
    (id: number) => {
      setChipFlies((f) => f.filter((x) => x.id !== id));
      pendingBootChipCountRef.current = Math.max(0, pendingBootChipCountRef.current - 1);
      if (pendingBootChipCountRef.current === 0 && bootUiPhaseRef.current === 'chips') {
        setBootUiPhase('pot');
        setBootChipsInFlight(false);
        prevPotRef.current = state.potAmount;
        bootChipRoundRef.current = state.roundNumber;
        playChipSound(0);
        window.setTimeout(() => playChipSound(0), 70);
      }
    },
    [state.potAmount, state.roundNumber, playChipSound]
  );

  const onCardDealt = useCallback((seatNo: number, cardRound: number) => {
    const hand = activeDealHandRef.current;
    if (hand <= 0 || hand !== state.battleId) return;
    play('deal');
    const count = Math.min(CARDS_PER_HAND, cardRound + 1);
    setDealtCardsBySeat((prev) => ({
      ...prev,
      [seatNo]: Math.max(prev[seatNo] ?? 0, count),
    }));
  }, [play, state.battleId]);
  const onChipLaunch = useCallback(() => {
    playChipSound(100);
  }, [playChipSound]);

  const timerSeat =
    displayState.sideShowPending?.targetSeat ?? displayState.activeSeat ?? null;
  const activeTurnPlayer = displayState.sideShowPending
    ? displayState.players.find(
        (p) => Number(p.seatNo) === Number(displayState.sideShowPending!.targetSeat)
      )
    : displayState.activePlayerUserId
      ? displayState.players.find((p) => p.userId === displayState.activePlayerUserId)
      : timerSeat != null
        ? displayState.players.find((p) => Number(p.seatNo) === Number(timerSeat))
        : undefined;
  const turnEpoch = `${displayState.roundNumber}-${activeTurnPlayer?.userId ?? 'none'}-${timerSeat ?? 'none'}`;
  const turnTimerActive =
    (displayState.phase === 'betting' || displayState.sideShowPending) &&
    activeTurnPlayer != null &&
    turnSeconds != null &&
    turnSeconds >= 0;

  const smoothHandDisplay =
    handCountdownSeconds != null ? handCountdownSeconds : state.handStartCountdown;

  const handCountdownLive =
    (smoothHandDisplay != null && smoothHandDisplay > 0) ||
    (state.handStartCountdown != null && state.handStartCountdown > 0);

  const sharCountdownLive =
    sharPhaseActive &&
    ((smoothHandDisplay != null && smoothHandDisplay > 0) ||
      (state.handStartCountdown != null && state.handStartCountdown > 0));

  const turnStatus: TurnStatus | undefined = sharCountdownLive
    ? {
        label: canActShar
          ? 'Vote Shar'
          : sharVote === 'accept'
            ? 'Accepted'
            : sharVote === 'decline'
              ? 'Declined'
              : 'Shar',
        seconds: Math.max(
          0,
          Math.ceil(
            handCountdownSeconds != null ? handCountdownSeconds : state.handStartCountdown ?? 0
          )
        ),
        urgent:
          (handCountdownSeconds ?? state.handStartCountdown ?? 0) <= 5,
        isMyTurn: canActShar,
      }
    : turnTimerActive
    ? {
        label: activeTurnPlayer?.userId === user?.userId ? 'You' : activeTurnPlayer?.username ?? '—',
        seconds: Math.max(0, Math.ceil(turnSeconds ?? 0)),
        urgent: (turnSeconds ?? 0) <= 5,
        isMyTurn: activeTurnPlayer?.userId === user?.userId,
      }
    : undefined;

  const winCelebrationBlocking = winSequenceActive || potCollecting;

  const showCenterSharTimer = sharCountdownLive && !winCelebrationBlocking;

  const showCenterHandTimer = nextHandTimerVisible;

  const centerHandCountdownDisplay =
    (showCenterHandTimer || showCenterSharTimer) &&
    smoothHandDisplay != null &&
    smoothHandDisplay > 0
      ? smoothHandDisplay
      : null;

  const centerTimerMax = showCenterSharTimer
    ? handCountdownMax
    : state.handStartSeconds ?? handCountdownMax;

  const centerTimerLabel = showCenterSharTimer
    ? 'Shar vote'
    : interRoundUiActive
      ? 'Next hand'
      : 'Starting';

  useEffect(() => {
    if (!animationsLiveRef.current) return;
    const { inCountdown } = resolveInterRoundSnapshot(state);
    if (!interRoundUiActive && !inCountdown) return;
    if (winPhase === 'idle' && !potCollecting) return;
    // Next-hand countdown started — clear win celebration for everyone (including winner)
    setWinPhase('idle');
    setPotCollecting(false);
    collectStartedRef.current = true;
    onClearBattleResult?.();
  }, [
    interRoundUiActive,
    state.interRoundPhase,
    state.handStartCountdown,
    state.handStartEndsAt,
    state.celebrationEndsAt,
    state.phase,
    winPhase,
    potCollecting,
    onClearBattleResult,
    state,
  ]);

  const showSeatTurnTimer =
    turnTimerActive &&
    turnSeconds != null &&
    turnSeconds >= 0 &&
    !showCenterHandTimer &&
    dealUiComplete;

  const wonPotAmount = useMemo(
    () =>
      Math.max(
        0,
        Number(battleResult?.potAmount ?? state.lastHandPotAmount ?? displayPot) || 0
      ),
    [battleResult?.potAmount, state.lastHandPotAmount, displayPot]
  );

  const tablePotAmount = useMemo(() => {
    if (winPhase === 'showdown' || winPhase === 'collect') {
      return Math.max(
        0,
        Number(
          displayPot ||
            battleResult?.potAmount ||
            state.lastHandPotAmount ||
            state.potAmount
        ) || 0
      );
    }
    if (winPhase === 'crown') {
      return 0;
    }

    const serverPot = Number(state.potAmount) || 0;
    const betSum = state.players.reduce((sum, p) => sum + Number(p.currentBet || 0), 0);
    const inActiveHand =
      state.phase === 'boot' ||
      state.phase === 'dealing' ||
      state.phase === 'betting';

    if (
      state.phase === 'waiting' ||
      (state.phase === 'completed' && !winSequenceActive) ||
      waitingForPlayers ||
      handStarting ||
      interRoundUiActive
    ) {
      return 0;
    }

    if (state.phase === 'boot' && (bootUiPhase === 'chips' || bootChipsInFlight)) return 0;

    if (inActiveHand) return Math.max(serverPot, betSum);
    return 0;
  }, [
    state.potAmount,
    state.players,
    state.phase,
    interRoundUiActive,
    handStarting,
    waitingForPlayers,
    bootUiPhase,
    bootChipsInFlight,
    winPhase,
    winSequenceActive,
    displayPot,
    battleResult?.potAmount,
    state.lastHandPotAmount,
  ]);

  const seatCardAnimate =
    revealHands && (winPhase === 'showdown' || winPhase === 'collect' || winPhase === 'crown')
      ? 'flip'
      : 'none';

  const showWinnerCrown =
    (winPhase === 'collect' || winPhase === 'crown') && !nextHandTimerVisible;
  const winnerSeatNo =
    winnerPlayer?.seatNo ?? state.players.find((p) => p.userId === winnerUserId)?.seatNo;
  const collectAmount = battleResult?.potAmount ?? displayPot ?? displayState.potAmount;
  const showdownOverlayActive =
    (winPhase === 'showdown' || winPhase === 'collect') && !nextHandTimerVisible;
  const winnerDisplayName = formatSeatPlayerName(
    battleResult?.winnerUsername ?? winnerPlayer?.username ?? 'Player',
    Boolean(user && winnerUserId != null && sameUserId(winnerUserId, user.userId))
  );
  const useSeatWinAnnounce = false;
  const showdownOverlayInfo =
    showdownOverlayActive && !useSeatWinAnnounce
      ? {
          mode: (winPhase === 'collect' ? 'collect-pot' : 'winner') as 'collect-pot' | 'winner',
          winnerName: winnerDisplayName,
          potAmount: collectAmount,
          handCategory: displayHandCategory(
            battleResult?.handCategory ?? state.handCategory
          ),
          isLocalWinner: Boolean(user && winnerUserId != null && sameUserId(winnerUserId, user.userId)),
        }
      : null;

  const bootCollectActive =
    state.phase === 'boot' && bootUiPhase === 'chips' && bootChipsInFlight;
  const bootCollectAmount = Math.max(
    Number(state.potAmount) || 0,
    bootAmount *
      Math.max(
        1,
        seatedPlayers.filter((p) => p.status === 'active' || p.status === 'all_in').length
      )
  );

  return (
    <div
      className="tp-desktop-root relative min-h-0 flex-1 overflow-hidden"
      onPointerDown={() => unlock()}
    >
      <AmbientEffects />

      <AnimatePresence>
        {sideShowResult ? (
          <SideShowCompareOverlay
            key={`${sideShowResult.battleUuid}-${sideShowResult.requester.seatNo}-${sideShowResult.target.seatNo}`}
            result={sideShowResult}
            viewerUserId={user?.userId}
          />
        ) : null}
      </AnimatePresence>

      <DesktopHeader
        roomName={displayState.tableName}
        roomCode={displayState.roomCode ?? fallbackRoomCode}
        variantCode={displayState.variantCode}
        battleUuid={
          displayState.battleId > 0 && !sharPhaseActive
            ? displayState.battleUuid
            : ''
        }
        bootAmount={bootAmount}
        potAmount={tablePotAmount}
        playersCount={seatedCount}
        maxPlayers={displayState.maxSeats ?? MAX_SEATS}
        roundNumber={sharPhaseActive ? 0 : displayState.roundNumber}
        turnStatus={turnStatus}
        balanceLabel={headerBalance}
        compactLandscape={layoutMode === 'mobile-landscape'}
        compactPortrait={layoutMode === 'mobile-portrait'}
        onBack={onBack ?? onLeave}
        onLeave={onLeave}
        onOpenWallet={onOpenWallet}
      />

      {sharPhaseActive && centerHandCountdownDisplay != null && centerHandCountdownDisplay > 0 && (
        <SharVoteBanner
          sharAmount={Number(displayState.sharAmount ?? 0)}
          secondsLeft={centerHandCountdownDisplay}
          canVote={canActShar}
          vote={sharVote}
        />
      )}

      <DesktopLeftSidebar />
      <DesktopRightSidebar
        actions={visibleRoundLogActions}
        roundNumber={
          sharPhaseActive
            ? 0
            : displayState.actionLogRoundNumber ?? displayState.roundNumber
        }
        currencySymbol={user?.currencySymbol}
        currencyCode={user?.currency}
      />
      <MobileSideDock />

      <div className="tp-arena-floor px-1 sm:px-2">
          <div className="tp-table-perspective w-full">
            <div className="tp-table-tilt mx-auto">
          <div className={`tp-table-stage${tableStageLayoutClass()}`}>
            <TableSurface liveMode layoutProfile={seatProfile} />

            <DistributorTray
              isDealing={dealAnimationActive}
              layoutProfile={seatProfile}
            />

            <TableCenterHub
              potAmount={tablePotAmount}
              bootAmount={bootAmount}
              isDealing={showDealingOverlay}
              phase={displayState.phase}
              lastBetAmount={betFlash?.amount}
              handCountdown={centerHandCountdownDisplay}
              handCountdownMax={centerTimerMax}
              handCountdownLabel={centerTimerLabel}
              timerOnly={Boolean(showCenterSharTimer || showCenterHandTimer)}
              winSequenceActive={winSequenceActive}
              winPhase={winPhase}
              potCollecting={potCollecting}
              compact={compactHub}
              layoutProfile={seatProfile}
            />

            {particleBurst && (
              <TableParticles
                key={particleBurst.id}
                active
                kind={particleBurst.kind}
                originX={particleBurst.x}
                originY={particleBurst.y}
                onDone={() => setParticleBurst(null)}
              />
            )}

            {showDealingOverlay && (
              <CardDealAnimation
                key={dealKey}
                dealKey={dealKey}
                seatNos={dealSeatNos}
                seatLayoutMap={seatLayoutMap}
                layoutProfile={seatProfile}
                onComplete={onDealComplete}
                onCardDealt={onCardDealt}
              />
            )}

            {chipFlies.length > 0 && (
              <div className="pointer-events-none absolute inset-0 z-[44] overflow-visible">
                {chipFlies.map((fly) => (
                  <ChipFlyAnimation
                    key={fly.id}
                    fromSeatNo={fly.fromSeatNo}
                    seatLayoutMap={seatLayoutMap}
                    seatProfile={seatProfile}
                    amount={fly.amount}
                    silent={fly.silent}
                    withSound={fly.withSound}
                    onDone={() => onChipFlyDone(fly.id)}
                    onLaunch={onChipLaunch}
                  />
                ))}
              </div>
            )}

            {potCollecting && winnerSeatNo != null && collectAmount > 0 && (
              <PotCollectAnimation
                key={`collect-${state.battleId}-${state.roundNumber}`}
                toSeatNo={winnerSeatNo}
                seatLayoutMap={seatLayoutMap}
                seatProfile={seatProfile}
                amount={collectAmount}
                onStart={onPotCollectStart}
                onChipLaunch={onPotCollectChipLaunch}
                onDone={onPotCollectDone}
              />
            )}

            <AnimatePresence>
              {bootCollectActive && bootCollectAmount > 0 ? (
                <ShowdownBanner
                  key={`boot-collect-${state.battleId}-${state.roundNumber}`}
                  variant="overlay"
                  layoutProfile={seatProfile}
                  mode="collect-boot"
                  potAmount={bootCollectAmount}
                />
              ) : null}
              {showdownOverlayInfo ? (
                <ShowdownBanner
                  key={`showdown-${state.battleId}-${state.roundNumber}-${winPhase}`}
                  variant="overlay"
                  layoutProfile={seatProfile}
                  {...showdownOverlayInfo}
                />
              ) : null}
            </AnimatePresence>

            {emptySeatMarkers.map(({ layout, seatNumber }) => (
              <div
                key={`empty-seat-${seatNumber}`}
                className={`tp-seat-anchor tp-seat-anchor-${layout.id} absolute z-30 overflow-visible`}
                style={emptySeatAnchorStyle(layout, seatProfile, seatNumber)}
              >
                <div className={seatScaleClass(layoutMode)}>
                  <div className={`tp-empty-seat-root tp-player-seat-${layout.id}`}>
                    <div className="tp-player-seat-stack tp-empty-seat-stack">
                      <EmptySeat
                        waiting={waitingForPlayers || handStarting}
                        seatNumber={seatNumber}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {seatAssignments.map(({ player, layout, isHero, feltSeatNumber }) => {
              if (!player) return null;
              const slotKey = `p-${player.userId}-${player.seatNo}`;
              const turnActive = isPlayerTurn(player);
              const turnTimerKey = `${turnEpoch}-${player.userId}`;
              const playerDealtCount = dealtCardsBySeat[player.seatNo] ?? 0;
              const playerHandVisible = handCardsFullyVisible || playerDealtCount > 0;
              return (
              <div key={slotKey}>
              <div
                className={`tp-seat-anchor tp-seat-anchor-${layout.id} absolute overflow-visible ${isHero ? 'z-[55]' : 'z-30'}`}
                style={seatAnchorStyle(layout, seatProfile, feltSeatNumber)}
              >
                <div className={seatScaleClass(layoutMode)}>
                  <PlayerSeat
                    variant={seatVariant}
                    player={player}
                    slot={isHero ? 'hero' : 'opponent'}
                    seatId={layout.id}
                    feltSeatNumber={feltSeatNumber}
                    isMe={sameUserId(player.userId, user?.userId)}
                    isTurn={turnActive}
                    turnTimerKey={turnTimerKey}
                    turnSeconds={turnActive && showSeatTurnTimer ? turnSeconds : null}
                    turnMaxSeconds={turnMaxSeconds}
                    inlineTurnTimer={inlineSeatTurnTimer}
                    hideCards={
                      hideSeatCards ||
                      state.roundNumber <= 0 ||
                      !playerHandVisible
                    }
                    roundKey={dealKey}
                    cardAnimate={seatCardAnimate}
                    justRevealed={justRevealed}
                    isWinner={showWinnerCrown && player.userId === winnerUserId}
                    showCrown={showWinnerCrown && player.userId === winnerUserId}
                    wonAmount={
                      !useSeatWinAnnounce &&
                      winPhase === 'crown' &&
                      player.userId === winnerUserId
                        ? wonPotAmount
                        : undefined
                    }
                    handLabel={resolveSeatHandLabel(player, {
                      sideShowResult: sideShowResult ?? null,
                      battleResult,
                      stateHandCategory: displayState.handCategory ?? undefined,
                      winnerUserId,
                      winPhase,
                      hideSeatCards,
                      phase: displayState.phase,
                    })}
                    actionFlash={
                      actionFlash != null && Number(player.userId) === actionFlash.userId
                        ? actionFlash.label
                        : null
                    }
                    actionFlashKey={
                      actionFlash != null && Number(player.userId) === actionFlash.userId
                        ? actionFlash.actionId
                        : 0
                    }
                    seatWinAnnounce={
                      useSeatWinAnnounce &&
                      winPhase === 'showdown' &&
                      winnerUserId != null &&
                      player.userId === winnerUserId &&
                      collectAmount > 0
                        ? {
                            title: `${winnerDisplayName} wins`,
                            amount: collectAmount,
                          }
                        : null
                    }
                    packedByTimeout={
                      !handUiSettled &&
                      player.status === 'folded' &&
                      state.lastTimedOutUserId != null &&
                      player.userId === state.lastTimedOutUserId
                    }
                    showSeeButton={
                      Boolean(
                        isHero &&
                          !player.isSeen &&
                          player.status !== 'folded' &&
                          displayState.phase === 'betting' &&
                          !actionPanelDealLocked &&
                          playerHandVisible
                      )
                    }
                    onSee={() => onAction('see')}
                    currentBet={
                      state.phase === 'boot' && (bootUiPhase === 'chips' || bootChipsInFlight)
                        ? 0
                        : (player.currentBet ?? 0)
                    }
                    betHighlight={betFlash?.seatNo === player.seatNo}
                    chatOpen={isHero && heroChatOpen}
                    onChatToggle={
                      isHero
                        ? () => setHeroChatOpen((open) => !open)
                        : undefined
                    }
                    onChatClose={isHero ? () => setHeroChatOpen(false) : undefined}
                    onSendChatPhrase={
                      isHero
                        ? (phraseId) => {
                            const phrase = QUICK_CHAT_PHRASES.find((p) => p.id === phraseId);
                            onSendChatPhrase?.(phraseId);
                            if (user?.userId != null && phrase) {
                              showSeatChatBubble({
                                id: `local-${Date.now()}`,
                                userId: Number(user.userId),
                                text: phrase.label,
                                kind: 'phrase',
                              });
                            }
                          }
                        : undefined
                    }
                    onSendChatSticker={
                      isHero
                        ? (stickerId) => {
                            const sticker = CHAT_STICKERS.find((s) => s.id === stickerId);
                            onSendChatSticker?.(stickerId);
                            if (user?.userId != null && sticker) {
                              showSeatChatBubble({
                                id: `local-${Date.now()}`,
                                userId: Number(user.userId),
                                text: sticker.emoji,
                                kind: 'sticker',
                                stickerId,
                              });
                            }
                          }
                        : undefined
                    }
                    seatChatBubble={seatChatBubbleByUserId.get(Number(player.userId)) ?? null}
                  />
                </div>
              </div>
              </div>
            );})}

          </div>
            </div>
          </div>
        </div>

      <div className="tp-table-footer-slot">
      <DesktopActionPanel
        phase={displayState.phase}
        waitingForPlayers={waitingForPlayers}
        seatedCount={seatedCount}
        eligibleCount={eligibleCount}
        bootAmount={bootAmount}
        disabled={actionsDisabled}
        isMyTurn={isMyTurn}
        isSeen={me?.isSeen ?? false}
        mySeatNo={me?.seatNo}
        activePlayers={activeCount}
        currentStake={betLevel}
        maxBet={displayState.maxBet}
        potLimit={displayState.potLimit}
        currentBet={me?.currentBet ?? 0}
        isDealing={actionPanelDealLocked}
        handStarting={handStarting}
        interRoundActive={interRoundUiActive}
        centerHandTimerActive={showCenterHandTimer}
        interRoundCountdown={
          smoothHandDisplay != null && smoothHandDisplay > 0
            ? Math.ceil(smoothHandDisplay)
            : undefined
        }
        interRoundWinnerName={
          interRoundUiActive && winnerPlayer
            ? (battleResult?.winnerUsername ?? winnerPlayer.username)
            : undefined
        }
        interRoundPotAmount={battleResult?.potAmount ?? displayState.potAmount}
        interRoundHandCategory={displayHandCategory(
          battleResult?.handCategory ?? state.handCategory
        )}
        winPhase={winPhase}
        isFolded={me?.status === 'folded'}
        sharAmount={displayState.sharAmount}
        sharVote={sharVote}
        sideShowPending={displayState.sideShowPending}
        legalActions={displayState.legalActions}
        betTiers={displayState.betTiers}
        onAction={onAction}
      />
      </div>
    </div>
  );
}
